/**
 * A door somebody was WAITING at has opened (owner item 3, 2026-08-27).
 *
 * THE MOMENT THE WHOLE WAITING ROOM EXISTS TO PRODUCE, and until now it was
 * invisible from the side that had been waiting. A registrant held a spot at a
 * handle, the handle's owner signed up and hung a door, and from the
 * registrant's console nothing at all happened: the demand record left the
 * board, the typed-handle road began answering "this door is live", and the one
 * line they had written went with both. They had to notice, find the handle
 * again, and write their knock from scratch. The strongest engagement moment in
 * the product was reaching them only by email, if they had an address at all.
 *
 * SO IT IS A SHELF, NOT A MESSAGE. `waiting/convert.ts` writes a `notice` row
 * into their own thread at conversion, which is the durable record and the
 * thing that bumps the row unread; this is the CALL TO ACTION, and it is here
 * for exactly the reason `prompts.tsx` gives for being here — a call to action
 * inside a scroller is a call to action you have to go and find. The notice
 * says it happened; the shelf is where it gets acted on.
 *
 * IT CARRIES THEIR OWN LINE, and that is the point of it rather than a nicety.
 * The line is what they already decided they wanted from this person, written
 * at a moment they were motivated enough to queue for it, and it is the draft
 * of the knock. Handing it back is the difference between "somebody opened a
 * door" and "the thing you asked for is one tap away".
 *
 * IT IS NOT A KNOCK AND NOTHING HERE MAKES ONE (WR H9). The button opens the
 * console's compose thread with the words in the box; the send is still the
 * sender's, and the `knocks` row is still `knocks/start.ts`'s to write — where
 * W10 links the registration to it, which is also what takes this row off the
 * shelf for good.
 */
import { useQuery } from "@tanstack/react-query";
import { openedWhileWaiting, type MyWaiting } from "../../lib/api";
import { myWaitingQuery } from "../../lib/queries";

export const OPENED_KICKER = "The door you were waiting at is open.";
export const KNOCK_FOR_REAL = "Knock for real";

/** "@maya hung a door." One sentence, one fact, no count and no rank. */
export function openedLine(handle: string): string {
  return `@${handle} hung a door.`;
}

/** What the row says under the handle when the line came back empty. */
export const NO_LINE_COPY = "You held a spot here.";

/**
 * The doors, read here rather than inside the shelf, because THE CALLER HAS TO
 * KNOW WHETHER THERE ARE ANY.
 *
 * The masthead is a bar with its own padding and its own blur, and the pane
 * draws it whenever it is handed anything at all — a component that returned
 * `null` internally would still be an element, and every thread in the product
 * would grow an empty 18px bar. So emptiness is answered before the element
 * exists.
 *
 * `enabled` is the same fact as the shelf's own action: with nowhere to send a
 * knock there is nothing to offer, so the request is not made either. That is
 * what keeps `DoormanArea` renderable outside the shell.
 */
export function useOpenedDoors(enabled: boolean): MyWaiting[] {
  const { data: mine = [] } = useQuery({ ...myWaitingQuery(), enabled, retry: false });
  return openedWhileWaiting(mine);
}

type Props = {
  /** The rows `useOpenedDoors` found. Never empty: the caller checked. */
  opened: MyWaiting[];
  /**
   * Take this line to that door. The shell's own compose thread — this
   * component knows nothing about drafts or navigation, because the two surfaces
   * that could offer this act must not each have their own idea of what it does.
   */
  onKnockAt: (handle: string, line: string) => void;
};

export function DoorsThatOpened({ opened, onKnockAt }: Props) {
  return (
    // `nob-prompts` for its top margin only. The setup shelf's own COLLAPSE
    // machinery (`nob-prompts-count`, `nob-prompts-list`) is deliberately not
    // borrowed: those jobs are chores an owner may reasonably scroll past, and
    // this is news with a shelf life. It stays whole.
    <div className="nob-prompts mx-auto w-full max-w-2xl px-6" data-testid="doors-that-opened">
      <p className="pb-1.5 text-sm font-semibold text-accent-ink">{OPENED_KICKER}</p>
      <ul className="flex list-none flex-col gap-2 pb-[5px] pr-[3px]">
        {opened.map((row) => (
          <OpenedRow key={row.handle} row={row} onKnockAt={onKnockAt} />
        ))}
      </ul>
    </div>
  );
}

/**
 * The shelf, standing on its own, for the two panes that are NOT a doorman
 * thread: the door step's invitation and the email knocker's identity card.
 *
 * Both belong to somebody with no door, which is precisely the registrant this
 * whole loop must not lose — they came here to reach one person, they queued
 * for it, and they have neither a briefing nor prompts nor a thread of their
 * own for the news to land in. It does its own read and answers `null` when
 * there is nothing, because unlike the masthead these panes cost nothing to
 * hand an empty element.
 */
export function OpenedDoorsShelf({ onKnockAt }: { onKnockAt: Props["onKnockAt"] }) {
  const opened = useOpenedDoors(true);
  if (opened.length === 0) return null;
  return (
    <div className="flex-none pt-2">
      <DoorsThatOpened opened={opened} onKnockAt={onKnockAt} />
    </div>
  );
}

function OpenedRow({ row, onKnockAt }: { row: MyWaiting; onKnockAt: Props["onKnockAt"] }) {
  const line = row.line.trim();
  return (
    <li>
      <button
        type="button"
        onClick={() => onKnockAt(row.handle, line)}
        className="nob-prompt-cta flex w-full items-center gap-3 rounded-2xl bg-accent
                   px-4 py-2 text-left text-sm font-semibold text-accent-fg
                   transition-colors duration-75 hover:bg-accent-soft active:bg-accent-strong
                   focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/70
                   focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
      >
        <span className="min-w-0 flex-1">
          <span className="block truncate">{openedLine(row.handle)}</span>
          {/* Their own words, quieter than the headline and clipped to one line:
              it is a reminder of what they asked for, not the draft itself. The
              draft is the box the button opens. */}
          <span className="block truncate text-xs font-medium opacity-75">
            {line || NO_LINE_COPY}
          </span>
        </span>
        {/* The verb, on the right of a row that is entirely the button. NOT
            underlined: an underline inside a filled pill reads as a link nested
            in a control, and there is only one control here. */}
        <span className="shrink-0 whitespace-nowrap">
          {KNOCK_FOR_REAL}
          <span aria-hidden="true"> →</span>
        </span>
      </button>
    </li>
  );
}
